import { useState, useRef } from 'react';
import { studentsApi } from '../services/api';

interface ProfilePictureUploadProps {
  studentId: number;
  currentPicture?: string | null;
  onUploadSuccess: (newPicturePath: string) => void;
  onDeleteSuccess: () => void;
}

export const ProfilePictureUpload = ({ 
  studentId, 
  currentPicture, 
  onUploadSuccess, 
  onDeleteSuccess 
}: ProfilePictureUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('File size too large. Maximum size is 5MB.');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const response = await studentsApi.updateProfilePicture(studentId, file);
      onUploadSuccess(response.data.profile_picture || '');
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } }; message?: string }).response?.data?.error || (err as { message?: string }).message || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this profile picture?')) return;

    setUploading(true);
    setError('');

    try {
      await studentsApi.deleteProfilePicture(studentId);
      onDeleteSuccess();
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } }; message?: string }).response?.data?.error || (err as { message?: string }).message || 'Failed to delete image');
    } finally {
      setUploading(false);
    }
  };

  const getImageUrl = (picturePath: string) => {
    // If it's already a full URL, return as is
    if (picturePath.startsWith('http')) {
      return picturePath;
    }
    // Otherwise, construct the full URL with backend origin
    return `${picturePath}`;
  };

  return (
    <div className="space-y-4">
      {/* Current Profile Picture */}
      <div className="flex items-center space-x-4">
        <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
          {currentPicture ? (
            <img 
              src={getImageUrl(currentPicture)} 
              alt="Profile" 
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-gray-400 text-xs">No Image</div>';
              }}
            />
          ) : (
            <div className="text-gray-400 text-xs">No Image</div>
          )}
        </div>
        
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-900">Profile Picture</h3>
          <p className="text-sm text-gray-500">
            Upload a profile picture. Max size: 5MB. Supported formats: JPEG, PNG, GIF, WebP.
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {/* Upload/Delete Actions */}
      <div className="flex items-center space-x-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          disabled={uploading}
          className="hidden"
        />
        
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? 'Uploading...' : currentPicture ? 'Change Picture' : 'Upload Picture'}
        </button>

        {currentPicture && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={uploading}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Delete Picture
          </button>
        )}
      </div>
    </div>
  );
};